import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Autocomplete,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import Cookies from 'js-cookie';
import { ArticleResponse, Category, Subcategory } from '../../types';

const ADMIN_COOKIE_KEY = 'zerde_admin_token';
const API_URL = 'https://guide.zerdegroup.kz/api/';

interface ArticleFormData {
  title: string;
  category_id: number | null;
  subcategory_id: number | null;
  content: string;
  is_published: boolean;
}

const ArticleForm: React.FC = () => {
  const navigate = useNavigate();
  const { articleId } = useParams();
  const isEditMode = Boolean(articleId);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);

  const [formData, setFormData] = useState<ArticleFormData>({
    title: '',
    category_id: null,
    subcategory_id: null,
    content: '',
    is_published: false,
  });

  // Загрузка категорий
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = Cookies.get(ADMIN_COOKIE_KEY);
        if (!token) {
          navigate('/auth');
          return;
        }

        const response = await fetch(`${API_URL}/categories`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Ошибка при загрузке категорий');
        }

        const data = await response.json();
        setCategories(Array.isArray(data) ? data : data.items || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке категорий');
      }
    };

    fetchCategories();
  }, [navigate]);

  // Загрузка подкатегорий при выборе категории
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!formData.category_id) {
        setSubcategories([]);
        return;
      }

      try {
        const token = Cookies.get(ADMIN_COOKIE_KEY);
        if (!token) {
          navigate('/auth');
          return;
        }

        const response = await fetch(`${API_URL}/subcategories/category/${formData.category_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Ошибка при загрузке подкатегорий');
        }

        const data = await response.json();
        setSubcategories(Array.isArray(data) ? data : data.items || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке подкатегорий');
      }
    };

    fetchSubcategories();
  }, [formData.category_id, navigate]);

  // Загрузка данных статьи в режиме редактирования
  useEffect(() => {
    const fetchArticle = async () => {
      if (!isEditMode || !articleId) return;

      try {
        setIsLoading(true);
        setError('');
        const token = Cookies.get(ADMIN_COOKIE_KEY);
        
        if (!token) {
          navigate('/auth');
          return;
        }

        const response = await fetch(`${API_URL}/articles/${articleId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Ошибка при загрузке статьи');
        }

        const article: ArticleResponse = await response.json();
        
        setFormData({
          title: article.title,
          category_id: article.category.id,
          subcategory_id: article.subcategory.id,
          content: article.content,
          is_published: article.is_published,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке статьи');
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticle();
  }, [isEditMode, articleId, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError('');
      const token = Cookies.get(ADMIN_COOKIE_KEY);

      if (!token) {
        navigate('/auth');
        return;
      }

      const url = isEditMode 
        ? `${API_URL}/articles/${articleId}`
        : `${API_URL}/articles`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Ошибка при ${isEditMode ? 'обновлении' : 'создании'} статьи`);
      }

      navigate('/admin/panel/articles');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && isEditMode && !formData.title) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
        {isEditMode ? 'Редактировать статью' : 'Создать статью'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Название статьи"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
              disabled={isLoading}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete
                fullWidth
                options={categories}
                value={categories.find(c => c.id === formData.category_id) || null}
                onChange={(_, newValue) => {
                  setFormData({
                    ...formData,
                    category_id: newValue?.id || null,
                    subcategory_id: null,
                  });
                }}
                getOptionLabel={(option) => option.title}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Категория"
                    required
                    disabled={isLoading}
                  />
                )}
              />

              <Autocomplete
                fullWidth
                options={subcategories}
                value={subcategories.find(s => s.id === formData.subcategory_id) || null}
                onChange={(_, newValue) => {
                  setFormData({
                    ...formData,
                    subcategory_id: newValue?.id || null,
                  });
                }}
                disabled={!formData.category_id || isLoading}
                getOptionLabel={(option) => option.title}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Подкатегория"
                    required
                    disabled={isLoading}
                  />
                )}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Содержание статьи
              </Typography>
              <Box data-color-mode="light">
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Для переноса строки нажмите Enter дважды. Для переноса внутри параграфа используйте два пробела в конце строки и Enter.
                </Typography>
                <MDEditor
                  value={formData.content}
                  onChange={(value) => setFormData({ ...formData, content: value || '' })}
                  height={500}
                  preview="live"
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    disabled={isLoading}
                  />
                }
                label={formData.is_published ? 'Опубликовано' : 'Черновик'}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/panel/articles')}
                  disabled={isLoading}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading || !formData.title || !formData.category_id || !formData.subcategory_id || !formData.content}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    formData.is_published ? 'Опубликовать' : 'Сохранить как черновик'
                  )}
                </Button>
              </Box>
            </Box>
          </Stack>
        </form>
    </Box>
  );
};

export default ArticleForm; 